import { Link } from "react-router-dom";
import styled from "styled-components";

export const FooterWrapper = styled.footer`
  position: relative;
  background: ${({ theme }) => theme.colors.navy};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) =>
    `${theme.spacing["4xl"]} ${theme.spacing["2xl"]} ${theme.spacing["2xl"]}`};
  overflow: hidden;

  /* Extend the navy behind the floating mobile quick-nav so there is no bare
     white strip below the footer (the bar sits ~64px tall + 10px offset). */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-bottom: calc(86px + env(safe-area-inset-bottom, 0px));
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(112, 141, 171, 0.4) 20%,
      rgba(201, 169, 110, 0.35) 50%,
      rgba(112, 141, 171, 0.4) 80%,
      transparent
    );
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        circle at 80% 0%,
        rgba(112, 141, 171, 0.12) 0%,
        transparent 45%
      ),
      radial-gradient(
        circle at 10% 100%,
        rgba(201, 169, 110, 0.05) 0%,
        transparent 45%
      );
    pointer-events: none;
  }

  & > * {
    position: relative;
  }
`;

export const FooterInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: ${({ theme }) => theme.spacing["3xl"]};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
    gap: ${({ theme }) => theme.spacing["2xl"]};
  }
`;

export const Brand = styled.div`
  img {
    height: 56px;
    width: auto;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }

  p {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    color: rgba(255, 255, 255, 0.55);
    line-height: ${({ theme }) => theme.lineHeights.relaxed};
    max-width: 320px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    img {
      margin-left: auto;
      margin-right: auto;
    }
    p {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

export const FooterCol = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    align-items: center;
  }
`;

export const ColTitle = styled.h4`
  position: relative;
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 24px;
    height: 1px;
    background: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;
export const SocialLink = styled.a`
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  color: rgba(255, 255, 255, 0.4);

  transition: all ${({ theme }) => theme.transitions.normal};

  svg {
    max-width: 18px;
    max-height: 18px;
    min-width: 18px;
    min-height: 18px;
  }

  &::before {
    content: "";
    width: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.primary};
    transition: width ${({ theme }) => theme.transitions.normal};
  }
  &:hover::before {
    width: 12px;
  }
  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const FooterLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  text-decoration: none;

  &::before {
    content: "";
    width: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.primary};
    transition: width ${({ theme }) => theme.transitions.normal};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primaryLight};
  }

  &:hover::before {
    width: 12px;
  }
`;

export const FooterText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

export const ContactGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    align-items: center;
  }
`;

export const ContactLabel = styled.span`
  display: block;
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 2px;
`;

export const Divider = styled.hr`
  border: none;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  margin: ${({ theme }) => theme.spacing["2xl"]} 0
    ${({ theme }) => theme.spacing.xl};
`;

export const FooterBottom = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  p {
    font-size: ${({ theme }) => theme.fontSizes.xs};
    color: rgba(255, 255, 255, 0.45);
    letter-spacing: ${({ theme }) => theme.letterSpacings.wide};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
    text-align: center;
  }
`;
